import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription
} from '@/components/ui/card';
import {
  ShieldCheck, Briefcase, Building2, ArrowLeft, ExternalLink, CheckCircle2, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const PATH_ICONS = {
  swedbank_sdi: ShieldCheck,
  self_employed: Briefcase,
  business: Building2,
};

const TYPE_FOR_PATH = {
  swedbank_sdi: 'self_employed',  // SDI is treated as self_employed legally
  self_employed: 'self_employed',
  business: 'business',
};

export default function SellerUpgradePage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [limits, setLimits] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [selectedPath, setSelectedPath] = useState(null);
  const [legalInfo, setLegalInfo] = useState({
    legal_name: '',
    registration_number: '',
    vid_registration_number: '',
    sdi_account: '',
    vat_number: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    axios.get(`${API}/seller/compliance/limits`).then(({ data }) => setLimits(data)).catch(() => {});
    axios.get(`${API}/seller/me/compliance`).then(({ data }) => setCompliance(data)).catch(() => {});
  }, [user, navigate]);

  const currentType = compliance?.seller_type || 'private';
  const isUpgraded = currentType === 'self_employed' || currentType === 'business';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPath) {
      toast.error('Lūdzu, izvēlies vienu no statusa veidiem.');
      return;
    }
    const newType = TYPE_FOR_PATH[selectedPath];
    const hasAny = Object.values(legalInfo).some((v) => (v || '').trim().length > 0);
    if (!hasAny) {
      toast.error('Lūdzu, ievadi vismaz vienu no juridiskajiem datiem (SDI konts, VID nr. vai reģ. nr.).');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/seller/me/status`, {
        seller_type: newType,
        seller_legal_info: { ...legalInfo, chosen_path: selectedPath },
      });
      toast.success(data.message || 'Statuss veiksmīgi atjaunināts!');
      if (checkAuth) await checkAuth();
      // Refetch compliance to show the updated state
      const { data: fresh } = await axios.get(`${API}/seller/me/compliance`);
      setCompliance(fresh);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kļūda saglabājot statusu.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="seller-upgrade-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/profile"
          className="inline-flex items-center gap-2 text-sm text-[#5C5C5C] hover:text-[#121212] mb-4"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Atpakaļ uz profilu
        </Link>

        <div className="bg-gradient-to-br from-[#FFF7ED] to-[#FFE4D1] border border-[#FBD8B5] rounded-2xl p-6 sm:p-8 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#D97736] flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#121212] mb-2">
                Pārdevēja statusa atjaunošana
              </h1>
              <p className="text-sm sm:text-base text-[#5C5C5C]">
                Lai turpinātu pārdot virs €{limits?.private?.yearly_revenue_eur || 2000} gadā vai vairāk nekā{' '}
                {limits?.private?.yearly_transactions || 30} darījumus, jāreģistrē saimnieciskās darbības forma.
                Izvēlies sev piemērotāko ceļu:
              </p>
            </div>
          </div>
        </div>

        {isUpgraded && (
          <div
            className="mb-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3"
            data-testid="upgraded-status-banner"
          >
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            <div className="text-sm">
              <strong className="text-emerald-800">Tu jau esi reģistrēts kā {currentType === 'business' ? 'uzņēmums (SIA/IK)' : 'pašnodarbinātais'}.</strong>{' '}
              <span className="text-emerald-700">Privātpersonas limiti uz tevi neattiecas. Vari atjaunot datus zemāk.</span>
            </div>
          </div>
        )}

        {/* Limits info card */}
        {limits && (
          <Card className="mb-6 border-[#E5E4E0]" data-testid="limits-info-card">
            <CardHeader>
              <CardTitle className="font-outfit text-lg">Kāpēc šie limiti?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-[#5C5C5C]">
              <p><strong className="text-[#121212]">€{limits.private.yearly_revenue_eur}/gadā:</strong> {limits.explanation_lv.yearly_revenue_eur}</p>
              <p><strong className="text-[#121212]">{limits.private.yearly_transactions} darījumi/gadā:</strong> {limits.explanation_lv.yearly_transactions}</p>
              <p><strong className="text-[#121212]">€{limits.private.monthly_payout_eur}/mēnesī:</strong> {limits.explanation_lv.monthly_payout_eur}</p>
            </CardContent>
          </Card>
        )}

        {/* Upgrade path picker */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {limits?.upgrade_paths?.map((path) => {
            const Icon = PATH_ICONS[path.id] || ShieldCheck;
            const isActive = selectedPath === path.id;
            return (
              <button
                key={path.id}
                type="button"
                onClick={() => setSelectedPath(path.id)}
                className={`text-left p-5 rounded-2xl border-2 transition-all ${
                  isActive
                    ? 'border-[#D97736] bg-[#FFF7ED] shadow-md'
                    : 'border-[#E5E4E0] bg-white hover:border-[#D97736]/50'
                }`}
                data-testid={`upgrade-path-${path.id}`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${
                  isActive ? 'bg-[#D97736] text-white' : 'bg-[#F5F4F1] text-[#D97736]'
                }`}>
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-outfit text-base font-semibold text-[#121212] mb-1">
                  {path.name_lv}
                </h3>
                <p className="text-xs text-[#5C5C5C] mb-3 line-clamp-4">
                  {path.description_lv}
                </p>
                <a
                  href={path.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-xs text-[#D97736] hover:underline"
                >
                  Uzzināt vairāk <ExternalLink className="w-3 h-3" />
                </a>
              </button>
            );
          })}
        </div>

        {/* Legal info form */}
        {selectedPath && (
          <Card className="border-[#E5E4E0]" data-testid="legal-info-form">
            <CardHeader>
              <CardTitle className="font-outfit text-lg">Juridiskie dati</CardTitle>
              <CardDescription>
                Ievadi vismaz vienu no šiem datiem. Pārējos vari aizpildīt vēlāk profila iestatījumos.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {selectedPath === 'swedbank_sdi' && (
                  <div>
                    <Label htmlFor="sdi_account">SDI konta numurs (IBAN)</Label>
                    <Input
                      id="sdi_account"
                      value={legalInfo.sdi_account}
                      onChange={(e) => setLegalInfo({ ...legalInfo, sdi_account: e.target.value })}
                      placeholder="LV00HABA0000000000000"
                      data-testid="sdi-account-input"
                    />
                  </div>
                )}

                {selectedPath === 'self_employed' && (
                  <div>
                    <Label htmlFor="vid_registration_number">VID reģistrācijas numurs</Label>
                    <Input
                      id="vid_registration_number"
                      value={legalInfo.vid_registration_number}
                      onChange={(e) => setLegalInfo({ ...legalInfo, vid_registration_number: e.target.value })}
                      placeholder="Piem., 12345678901"
                      data-testid="vid-registration-input"
                    />
                  </div>
                )}

                {selectedPath === 'business' && (
                  <>
                    <div>
                      <Label htmlFor="legal_name">Juridiskais nosaukums</Label>
                      <Input
                        id="legal_name"
                        value={legalInfo.legal_name}
                        onChange={(e) => setLegalInfo({ ...legalInfo, legal_name: e.target.value })}
                        placeholder="SIA / IK pilns nosaukums"
                        data-testid="legal-name-input"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="registration_number">Reģistrācijas numurs</Label>
                        <Input
                          id="registration_number"
                          value={legalInfo.registration_number}
                          onChange={(e) => setLegalInfo({ ...legalInfo, registration_number: e.target.value })}
                          placeholder="40003000000"
                          data-testid="registration-number-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="vat_number">PVN reģ. nr. (neobligāts)</Label>
                        <Input
                          id="vat_number"
                          value={legalInfo.vat_number}
                          onChange={(e) => setLegalInfo({ ...legalInfo, vat_number: e.target.value })}
                          placeholder="LV40003000000"
                          data-testid="vat-number-input"
                        />
                      </div>
                    </div>
                  </>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#D97736] hover:bg-[#C06528] text-white"
                  data-testid="submit-upgrade-btn"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Saglabā...
                    </span>
                  ) : (
                    'Apstiprināt statusa atjaunošanu'
                  )}
                </Button>

                <p className="text-xs text-[#5C5C5C] text-center">
                  Pēc apstiprināšanas paslēptie sludinājumi tiks automātiski atjaunoti.
                </p>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
